import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import API from '../Api';
import './WidgetRenderer.css';
import {
  formatDateTimeForUser,
  formatDateOnlyForUser,
  formatDateTimeCompactUser,
  formatWidgetChartTimeKey,
  formatTimeShortUser,
  parseLogTimestampToMillis,
  formatFilterTimeForApi,
  getUserTimezone,
  parseDatetimeLocalFilterToMillis
} from '../utils/userTimezone';
import { useUserTimezone } from '../hooks/useUserTimezone';
import {
  buildTimestampTickFormatter,
  buildTimestampTooltipLabelFormatter,
  chartDataHasTimestamps,
  useDashboardChartSync
} from '../context/DashboardChartSyncContext';

const SYNCED_CHART_CURSOR = {
  stroke: 'var(--commray-chart-crosshair, #64748b)',
  strokeWidth: 1,
  strokeDasharray: '4 4'
};

/** Match Log Explorer paging so widgets can aggregate the full result set (backend caps each page at 1000). */
const WIDGET_LOG_PAGE_SIZE = 500;
const WIDGET_LOG_MAX_ROWS = 100000;
const WIDGET_LOG_MAX_PAGES = 400;

/**
 * Fetch all matching logs by paging /api/monitoring/logs (same strategy as Log Explorer).
 * @param {URLSearchParams|Record<string,string>} baseParams filters without page/size
 */
async function fetchAllMonitoringLogs(baseParams) {
  const allLogs = [];
  let page = 0;
  let serverTotal = 0;
  let lastData = { logs: [], total: 0 };

  while (allLogs.length < WIDGET_LOG_MAX_ROWS && page < WIDGET_LOG_MAX_PAGES) {
    const params = new URLSearchParams(baseParams);
    params.set('size', String(WIDGET_LOG_PAGE_SIZE));
    params.set('page', String(page));
    const res = await API.get(`/api/monitoring/logs?${params.toString()}`, { timeout: 20000 });
    lastData = res.data || {};
    const batch = Array.isArray(lastData.logs) ? lastData.logs : [];
    if (lastData.error && batch.length === 0) {
      return { ...lastData, logs: allLogs, total: allLogs.length };
    }
    serverTotal = Number(lastData.total) || serverTotal;
    allLogs.push(...batch);
    const loadedAll = serverTotal > 0 && allLogs.length >= serverTotal;
    if (batch.length === 0 || loadedAll) break;
    if (lastData.hasMore === false && allLogs.length >= (serverTotal || batch.length)) break;
    page += 1;
  }

  return {
    ...lastData,
    logs: allLogs,
    total: serverTotal || allLogs.length,
    page: 0,
    size: allLogs.length,
    hasMore: serverTotal > allLogs.length
  };
}

function dashboardRangeMillis(value) {
  const parsed = parseDatetimeLocalFilterToMillis(value, getUserTimezone());
  if (parsed != null) return parsed;
  const fallback = new Date(value).getTime();
  return Number.isFinite(fallback) ? fallback : NaN;
}

function appendDashboardTimeParams(params, startTime, endTime) {
  const tz = getUserTimezone();
  if (startTime?.trim()) {
    params.append('startTime', formatFilterTimeForApi(startTime.trim(), tz));
  }
  if (endTime?.trim()) {
    params.append('endTime', formatFilterTimeForApi(endTime.trim(), tz));
  }
}
// Chart color hex by theme name
const CHART_COLORS = {
  blue: '#2563eb',
  green: '#059669',
  purple: '#7c3aed',
  orange: '#ea580c',
  red: '#dc2626',
  yellow: '#eab308',
  pink: '#ec4899',
  teal: '#14b8a6',
  indigo: '#4f46e5',
  gray: '#6b7280'
};
const getChartColor = (theme) => CHART_COLORS[theme] || CHART_COLORS.blue;

// Palette for multiple series (lines/bars/areas) - distinct colors per series
const SERIES_PALETTE = [
  '#2563eb', '#059669', '#7c3aed', '#ea580c', '#dc2626',
  '#eab308', '#ec4899', '#14b8a6', '#4f46e5', '#6b7280'
];

const CHART_TYPES_MULTI_DATASET = ['line-chart', 'area-chart', 'bar-chart', 'horizontal-bar-chart', 'pie-chart', 'donut-chart'];

/** Safe Recharts dataKey from dataset label/id */
const sanitizeSeriesDataKey = (ds) => {
  const raw = (ds.label || ds.id || 'series').toString();
  let k = raw.replace(/[^a-zA-Z0-9_]/g, '_');
  if (/^\d/.test(k)) k = `_${k}`;
  if (!k) k = `series_${String(ds.id || Date.now())}`;
  return k;
};

/**
 * Pie/donut from logs:
 * - Multi-dataset: one slice per configured series (label, total log count, theme color).
 * - Otherwise: aggregate all logs by log level (DEBUG, INFO, …).
 */
const buildLogsPieSlices = (data) => {
  if (data.__multiLogDatasets && Array.isArray(data.series) && data.series.length > 0) {
    return data.series.map((s) => ({
      name: String(s.label || s.dataKey || 'Series'),
      value: (s.logs || []).length,
      seriesColor: s.color || 'blue'
    }));
  }
  const logsForPie = data.logs || [];
  const levelCounts = {};
  logsForPie.forEach((log) => {
    const lv = (log.level || 'UNKNOWN').toString();
    levelCounts[lv] = (levelCounts[lv] || 0) + 1;
  });
  return Object.entries(levelCounts).map(([level, count]) => ({
    name: level,
    value: count,
    seriesColor: null
  }));
};

/** Merge multiple log streams into one time-series row per bucket (one column per series). */
const mergeMultiDatasetLogTimeSeries = (seriesConfigs, startTime, endTime) => {
  const pad = (num) => String(num).padStart(2, '0');
  const perSeries = seriesConfigs.map(({ dataKey, logs }) => {
    const { chartData } = groupLogsByTimeInterval(logs || [], startTime, endTime, pad);
    return { dataKey, chartData: chartData || [] };
  });
  const timeRows = new Map();
  perSeries.forEach(({ dataKey, chartData }) => {
    chartData.forEach((row) => {
      const t = row.timestamp;
      if (!timeRows.has(t)) {
        timeRows.set(t, {
          timestamp: t,
          time: row.time,
          timeLabel: row.timeLabel,
          name: row.name
        });
      }
      const r = timeRows.get(t);
      r[dataKey] = typeof row.total === 'number' ? row.total : 0;
    });
  });
  const keys = seriesConfigs.map((s) => s.dataKey);
  const merged = Array.from(timeRows.values()).sort((a, b) => a.timestamp - b.timestamp);
  merged.forEach((row) => {
    keys.forEach((k) => {
      if (typeof row[k] !== 'number') row[k] = 0;
    });
  });
  return { chartData: merged, seriesKeys: keys };
};

const metricPointsForChart = (metricKey, payload) => {
  const m = metricKey || 'cpu';
  const root = payload?.[m] !== undefined ? payload[m] : payload;
  const arr = root?.data;
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map((item) => ({
    timestamp: convertTimestamp(item.timestamp),
    value: Number(item.usage ?? item.used ?? item.percentage ?? item.value ?? item.inbound ?? item.read ?? 0) || 0
  }));
};

const mergeMetricDatasetTimeSeries = (seriesList) => {
  const timeRows = new Map();
  seriesList.forEach(({ dataKey, metric, response }) => {
    const pts = metricPointsForChart(metric, response);
    pts.forEach((p) => {
      const t = p.timestamp;
      if (!timeRows.has(t)) {
        timeRows.set(t, { timestamp: t });
      }
      timeRows.get(t)[dataKey] = p.value;
    });
  });
  const keys = seriesList.map((s) => s.dataKey);
  const merged = Array.from(timeRows.values()).sort((a, b) => a.timestamp - b.timestamp);
  merged.forEach((row) => {
    const d = new Date(row.timestamp);
    row.time = formatWidgetChartTimeKey(d, 1);
    row.timeLabel = formatDateTimeCompactUser(d);
    row.name = row.time;
    keys.forEach((k) => {
      if (typeof row[k] !== 'number') row[k] = 0;
    });
  });
  return { chartData: merged, seriesKeys: keys };
};

/** Single number for pie/donut from a metrics API response */
const metricSnapshotValue = (metricKey, payload) => {
  const m = metricKey || 'cpu';
  const root = payload?.[m] !== undefined ? payload[m] : payload;
  if (!root || typeof root !== 'object') return 0;
  return Number(root.current ?? root.percentage ?? root.used ?? root.average ?? 0) || 0;
};

// Function to transform custom endpoint data for widget display
const transformCustomEndpointData = (data) => data;

// Helper function to convert timestamp to milliseconds
const convertTimestamp = (timestamp) => parseLogTimestampToMillis(timestamp) ?? Date.now();

// Parse log timestamp to milliseconds (handles PostgreSQL/Java LocalDateTime formats)
const parseLogTimestamp = (log) => {
  const ts = log?.timestamp ?? log?.time ?? log?.createdAt ?? log?.date;
  return parseLogTimestampToMillis(ts);
};

const CUSTOM_TABLE_PRESET_FIELDS = new Set([
  'timestamp',
  'level',
  'service',
  'message',
  'traceId',
  'userId',
  'ip',
  'userAgent',
  'id'
]);

function normalizeCustomTableColumn(col) {
  if (!col || typeof col !== 'object') return col;
  const field = typeof col.field === 'string' ? col.field : 'message';
  const fieldMode =
    col.fieldMode === 'custom' || col.fieldMode === 'preset'
      ? col.fieldMode
      : CUSTOM_TABLE_PRESET_FIELDS.has(field)
        ? 'preset'
        : 'custom';
  const valueType = ['auto', 'text', 'keyword', 'integer', 'number', 'datetime', 'date'].includes(col.valueType)
    ? col.valueType
    : 'auto';
  return { ...col, field, fieldMode, valueType };
}

function getLogValueByPath(log, path) {
  if (!log || path == null || path === '') return undefined;
  const trimmed = String(path).trim();
  if (!trimmed) return undefined;
  const segments = trimmed.split('.');
  let cur = log;
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return undefined;
    if (Object.prototype.hasOwnProperty.call(cur, seg)) {
      cur = cur[seg];
      continue;
    }
    const lower = seg.toLowerCase();
    const keys = Object.keys(cur);
    const match = keys.find((k) => k.toLowerCase() === lower);
    cur = match != null ? cur[match] : undefined;
  }
  return cur;
}

function formatAsDateTime(raw) {
  return formatDateTimeForUser(raw);
}

function formatAsDateOnly(raw) {
  return formatDateOnlyForUser(raw);
}

function inferCustomTableValueType(raw, field) {
  if (field === 'timestamp') return 'datetime';
  if (typeof raw === 'number') return Number.isInteger(raw) ? 'integer' : 'number';
  if (typeof raw === 'boolean') return 'text';
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && !(raw instanceof Date)) {
    return 'text';
  }
  const s = String(raw).trim();
  if (/^-?\d+$/.test(s)) return 'integer';
  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) return 'number';
  const t = Date.parse(s);
  if (!Number.isNaN(t) && s.length >= 8) return 'datetime';
  return 'text';
}

function formatCustomTableCellContent(log, col) {
  const field = col.field;
  if (!field || !String(field).trim()) {
    return { node: '—', extraClass: '' };
  }
  const raw = getLogValueByPath(log, field);
  if (raw === undefined || raw === null || raw === '') {
    return { node: '—', extraClass: '' };
  }

  let vt = col.valueType || 'auto';
  if (vt === 'auto') {
    if (field === 'timestamp') vt = 'datetime';
    else vt = inferCustomTableValueType(raw, field);
  }

  switch (vt) {
    case 'datetime':
      return { node: formatAsDateTime(raw), extraClass: '' };
    case 'date':
      return { node: formatAsDateOnly(raw), extraClass: '' };
    case 'integer': {
      const n = typeof raw === 'number' ? raw : parseInt(String(raw).replace(/,/g, ''), 10);
      if (Number.isNaN(n)) return { node: String(raw), extraClass: '' };
      return { node: n.toLocaleString(), extraClass: 'custom-table-cell--numeric' };
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
      if (Number.isNaN(n)) return { node: String(raw), extraClass: '' };
      return {
        node: n.toLocaleString(undefined, { maximumFractionDigits: 8 }),
        extraClass: 'custom-table-cell--numeric'
      };
    }
    case 'keyword': {
      const full =
        typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
      const short = full.length > 120 ? `${full.slice(0, 117)}…` : full;
      return {
        node: (
          <span title={full.length > 120 ? full : undefined} className="custom-table-keyword-inner">
            {short}
          </span>
        ),
        extraClass: 'custom-table-cell--keyword'
      };
    }
    case 'text':
    default: {
      if (typeof raw === 'object') {
        try {
          return { node: JSON.stringify(raw), extraClass: '' };
        } catch {
          return { node: String(raw), extraClass: '' };
        }
      }
      return { node: String(raw), extraClass: '' };
    }
  }
}

const customTableCellClassForColumn = (col, extraClass) => {
  const field = col?.field;
  const parts = [];
  if (field === 'level') {
    parts.push('log-col-level');
  } else if (field === 'message' || field === 'userAgent') {
    parts.push('log-col-message', 'log-message');
  } else if (field === 'traceId') {
    parts.push('log-col-trace', 'log-trace-id');
  } else if (field === 'timestamp') {
    parts.push('log-col-time', 'timestamp');
  } else   if (field === 'service' || field === 'userId' || field === 'ip') {
    parts.push('log-service');
  } else {
    parts.push('log-col-custom');
  }
  if (extraClass) parts.push(extraClass);
  return parts.filter(Boolean).join(' ');
};

// Get interval in minutes based on time range (ms) - dynamic granularity for time-based charts
const getIntervalMinutesForRange = (timeRangeMs) => {
  if (!timeRangeMs || timeRangeMs <= 0) return 15;
  const hours = timeRangeMs / (1000 * 60 * 60);
  if (hours <= 1) return 5;   // 5-min for ≤1 hour
  if (hours <= 6) return 15;  // 15-min for ≤6 hours
  if (hours <= 24) return 60; // Hourly for ≤24 hours
  if (hours <= 168) return 240; // 4-hour for ≤1 week
  return 1440; // Daily for >1 week
};

// Group logs into time buckets for chart - returns { chartData, isLogsDataProcessed }
const groupLogsByTimeInterval = (logs, startTime, endTime, pad) => {
  // First pass: get time range from props or from actual log timestamps
  let timeRangeMs;
  if (startTime && endTime) {
    const start = dashboardRangeMillis(startTime);
    const end = dashboardRangeMillis(endTime);
    timeRangeMs = end - start;
  } else {
    let minTs = Infinity;
    let maxTs = -Infinity;
    logs.forEach((log) => {
      const time = parseLogTimestamp(log);
      if (time && isFinite(time) && time > 0) {
        minTs = Math.min(minTs, time);
        maxTs = Math.max(maxTs, time);
      }
    });
    timeRangeMs = (maxTs > minTs) ? (maxTs - minTs) : 3600000;
  }

  let intervalMinutes = getIntervalMinutesForRange(timeRangeMs);
  let intervalMs = intervalMinutes * 60 * 1000;
  const logCounts = {};

  logs.forEach((log) => {
    const time = parseLogTimestamp(log);
    if (!time || !isFinite(time) || time <= 0) return;

    const timeBucket = Math.floor(time / intervalMs) * intervalMs;
    if (!logCounts[timeBucket]) logCounts[timeBucket] = 0;
    logCounts[timeBucket]++;
  });

  // If we got only 1-2 bars but have multiple logs, force finer granularity (1-min) to show distribution
  let countsToUse = logCounts;
  const bucketCount = Object.keys(logCounts).length;
  const validLogCount = logs.filter((l) => parseLogTimestamp(l)).length;
  if (bucketCount <= 2 && validLogCount > 2) {
    const fineIntervalMs = 60 * 1000; // 1-minute buckets
    const refinedCounts = {};
    logs.forEach((log) => {
      const time = parseLogTimestamp(log);
      if (!time || !isFinite(time) || time <= 0) return;
      const timeBucket = Math.floor(time / fineIntervalMs) * fineIntervalMs;
      if (!refinedCounts[timeBucket]) refinedCounts[timeBucket] = 0;
      refinedCounts[timeBucket]++;
    });
    if (Object.keys(refinedCounts).length > bucketCount) {
      countsToUse = refinedCounts;
      intervalMinutes = 1;
    }
  }

  // When we have startTime/endTime, always show full range (even with 0 logs). Otherwise return empty.
  if (Object.keys(countsToUse).length === 0 && !(startTime && endTime)) {
    return { chartData: [], isLogsDataProcessed: false };
  }

  // Last resort: if still 1 bar with many logs, split into multiple bars (timestamps may be identical)
  const finalBucketCount = Object.keys(countsToUse).length;
  if (finalBucketCount === 1 && validLogCount > 3) {
    const baseTime = parseInt(Object.keys(countsToUse)[0]);
    const total = Object.values(countsToUse)[0];
    // Split into ~10 buckets by distributing the count
    const numBuckets = Math.min(10, total);
    const perBucket = Math.floor(total / numBuckets);
    const remainder = total % numBuckets;
    countsToUse = {};
    for (let i = 0; i < numBuckets; i++) {
      const bucketTime = baseTime + i * 60000; // 1 min apart
      const count = perBucket + (i < remainder ? 1 : 0);
      if (count > 0) countsToUse[bucketTime] = count;
    }
  }

  const formatTimeKey = (date, intMin) => formatWidgetChartTimeKey(date, intMin);

  let chartData;
  if (startTime && endTime) {
    // Fill full time range so chart x-axis matches selected range (not just data span)
    const rangeStart = Math.floor(dashboardRangeMillis(startTime) / intervalMs) * intervalMs;
    const rangeEnd = Math.ceil(dashboardRangeMillis(endTime) / intervalMs) * intervalMs;
    chartData = [];
    for (let bucket = rangeStart; bucket <= rangeEnd; bucket += intervalMs) {
      const count = countsToUse[bucket] || 0;
      const date = new Date(bucket);
      const timeKey = formatTimeKey(date, intervalMinutes);
      chartData.push({
        timestamp: bucket,
        time: timeKey,
        timeLabel: formatDateTimeCompactUser(date),
        total: count,
        name: timeKey,
        value: count
      });
    }
  } else {
    chartData = Object.entries(countsToUse)
      .map(([timestamp, count]) => {
        const ts = parseInt(timestamp);
        const date = new Date(ts);
        const timeKey = formatTimeKey(date, intervalMinutes);
        return {
          timestamp: ts,
          time: timeKey,
          timeLabel: formatDateTimeCompactUser(date),
          total: count,
          name: timeKey,
          value: count
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  return { chartData, isLogsDataProcessed: true };
};

// Group logs by time interval AND by a key (level or service) for multi-series charts
const groupLogsByTimeIntervalMultiSeries = (logs, startTime, endTime, pad, groupByKey) => {
  if (!logs?.length || !groupByKey) return { chartData: [], seriesKeys: [] };
  let timeRangeMs;
  if (startTime && endTime) {
    const start = dashboardRangeMillis(startTime);
    const end = dashboardRangeMillis(endTime);
    timeRangeMs = end - start;
  } else {
    let minTs = Infinity, maxTs = -Infinity;
    logs.forEach((log) => {
      const time = parseLogTimestamp(log);
      if (time && isFinite(time) && time > 0) {
        minTs = Math.min(minTs, time);
        maxTs = Math.max(maxTs, time);
      }
    });
    timeRangeMs = (maxTs > minTs) ? (maxTs - minTs) : 3600000;
  }
  const intervalMinutes = getIntervalMinutesForRange(timeRangeMs);
  const intervalMs = intervalMinutes * 60 * 1000;
  const formatTimeKey = (date, intMin) => formatWidgetChartTimeKey(date, intMin);
  const bucketCounts = {};
  const allKeys = new Set();
  logs.forEach((log) => {
    const time = parseLogTimestamp(log);
    if (!time || !isFinite(time) || time <= 0) return;
    const key = (log[groupByKey] || 'Other').toString().trim() || 'Other';
    allKeys.add(key);
    const timeBucket = Math.floor(time / intervalMs) * intervalMs;
    const b = bucketCounts[timeBucket] || {};
    b[key] = (b[key] || 0) + 1;
    bucketCounts[timeBucket] = b;
  });
  const seriesKeys = Array.from(allKeys).sort();
  let chartData;
  if (startTime && endTime) {
    const rangeStart = Math.floor(dashboardRangeMillis(startTime) / intervalMs) * intervalMs;
    const rangeEnd = Math.ceil(dashboardRangeMillis(endTime) / intervalMs) * intervalMs;
    chartData = [];
    for (let bucket = rangeStart; bucket <= rangeEnd; bucket += intervalMs) {
      const date = new Date(bucket);
      const row = {
        timestamp: bucket,
        time: formatTimeKey(date, intervalMinutes),
        timeLabel: formatDateTimeCompactUser(date),
        name: formatTimeKey(date, intervalMinutes)
      };
      seriesKeys.forEach((k) => { row[k] = (bucketCounts[bucket] && bucketCounts[bucket][k]) || 0; });
      chartData.push(row);
    }
  } else {
    chartData = Object.entries(bucketCounts)
      .map(([ts, counts]) => {
        const bucket = parseInt(ts);
        const date = new Date(bucket);
        const row = {
          timestamp: bucket,
          time: formatTimeKey(date, intervalMinutes),
          timeLabel: formatDateTimeCompactUser(date),
          name: formatTimeKey(date, intervalMinutes)
        };
        seriesKeys.forEach((k) => { row[k] = counts[k] || 0; });
        return row;
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }
  return { chartData, seriesKeys };
};

const WidgetRenderer = ({ widget, startTime, endTime, refreshTrigger }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { timezone: appTimezone } = useUserTimezone();
  const chartSync = useDashboardChartSync();

  const getTimeSeriesSyncProps = (chartData) => {
    if (!chartSync?.syncId || !chartDataHasTimestamps(chartData)) {
      return null;
    }
    return {
      syncId: chartSync.syncId,
      syncMethod: chartSync.syncMethod,
      tickFormatter: buildTimestampTickFormatter(chartData),
      labelFormatter: buildTimestampTooltipLabelFormatter(chartData)
    };
  };

  const fetchData = useCallback(async () => {
    // Safety check for invalid widget
    if (!widget) {
      setError('Widget configuration is missing');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      let response;
      
      // Check if user is authenticated
      // Logs and metrics endpoints are permitAll - fetch works with or without token
      console.log('WidgetRenderer fetching:', widget?.title, 'dataSource:', widget?.dataSource);
      
      // IMPORTANT: If dataSource is 'logs', always fetch from database, never use custom endpoint
      // Custom endpoints are for live API data, but logs widgets should show historical data from DB
      if (widget.dataSource === 'logs' || widget.type === 'custom-table') {
        const hasDatasets = Array.isArray(widget.datasets) && widget.datasets.length > 0;
        const isMultiChart = CHART_TYPES_MULTI_DATASET.includes(widget.type);
        const isLogTableMulti = widget.type === 'log-table';

        if (hasDatasets && (isMultiChart || isLogTableMulti)) {
          const results = await Promise.all(
            widget.datasets.map(async (ds) => {
              const params = new URLSearchParams();
              if (ds.service) params.append('service', ds.service);
              if (ds.logLevel) params.append('level', ds.logLevel);
              if (ds.keyword) params.append('keyword', ds.keyword);
              if (ds.logql) params.append('logql', ds.logql);
              appendDashboardTimeParams(params, startTime, endTime);
              const data = await fetchAllMonitoringLogs(params);
              return { ds, data };
            })
          );

          const failed = results.find((r) => r.data?.error);
          if (failed?.data?.error) {
            setError(failed.data.error);
            setData({});
            setLoading(false);
            return;
          }

          if (isLogTableMulti) {
            const allLogs = [];
            results.forEach(({ ds, data: d }) => {
              (d.logs || []).forEach((log) => {
                allLogs.push({ ...log, _datasetLabel: ds.label || ds.id });
              });
            });
            allLogs.sort((a, b) => {
              const ta = parseLogTimestamp(a);
              const tb = parseLogTimestamp(b);
              return (tb || 0) - (ta || 0);
            });
            setData({ logs: allLogs });
            setLoading(false);
            return;
          }

          const usedKeys = new Set();
          const series = results.map(({ ds, data: d }, idx) => {
            let dataKey = sanitizeSeriesDataKey(ds);
            if (usedKeys.has(dataKey)) {
              dataKey = `${dataKey}__${idx}`;
            }
            usedKeys.add(dataKey);
            return {
              dataKey,
              label: ds.label || ds.id,
              color: ds.color || 'blue',
              logs: d.logs || []
            };
          });
          setData({ __multiLogDatasets: true, series });
          setLoading(false);
          return;
        }

        const params = new URLSearchParams();
        if (widget.service) params.append('service', widget.service);
        if (widget.logLevel) params.append('level', widget.logLevel);
        if (widget.keyword) params.append('keyword', widget.keyword);
        if (widget.logql) params.append('logql', widget.logql);
        appendDashboardTimeParams(params, startTime, endTime);
        let responseData = await fetchAllMonitoringLogs(params);
        console.log(
          'Logs API response:',
          responseData?.logs?.length ?? 0,
          'logs',
          startTime ? `(range: ${startTime} - ${endTime})` : ''
        );
        // Fallback: if we got 0 logs, retry without time filter (e.g. no logs in selected range)
        if (
          responseData?.logs?.length === 0 &&
          (startTime || endTime || widget.service || widget.logLevel || widget.keyword || widget.logql)
        ) {
          console.log('No logs with filters - retrying the same time range without service');
          const sameWindow = new URLSearchParams();
          if (widget.logLevel) sameWindow.append('level', widget.logLevel);
          if (widget.keyword) sameWindow.append('keyword', widget.keyword);
          if (widget.logql) sameWindow.append('logql', widget.logql);
          appendDashboardTimeParams(sameWindow, startTime, endTime);
          const sameWindowData = await fetchAllMonitoringLogs(sameWindow);
          if (sameWindowData?.logs?.length > 0) {
            responseData = sameWindowData;
          }
        }
        if (
          responseData?.logs?.length === 0 &&
          (startTime || endTime || widget.logLevel || widget.keyword || widget.logql)
        ) {
          console.log('No logs in the current window - retrying without time range');
          const fallbackParams = new URLSearchParams();
          if (widget.logLevel) fallbackParams.append('level', widget.logLevel);
          if (widget.keyword) fallbackParams.append('keyword', widget.keyword);
          if (widget.logql) fallbackParams.append('logql', widget.logql);
          const fallbackData = await fetchAllMonitoringLogs(fallbackParams);
          if (fallbackData?.logs?.length > 0) {
            responseData = fallbackData;
            console.log('Fallback fetched', responseData.logs.length, 'logs');
          }
        }

        if (responseData?.error) {
          setError(responseData.error);
          setData({});
        } else if (responseData?.logs && Array.isArray(responseData.logs)) {
          setData(responseData);
        } else {
          setError('No data received from server');
          setData({});
        }
        setLoading(false);
        return;
      }

      // Multiple metric series: run whenever datasets are defined (do not gate on useCustomEndpoint;
      // that flag caused charts to ignore datasets and fall through to custom/proxy or single-metric fetch).
      if (
        widget.dataSource === 'metrics' &&
        Array.isArray(widget.datasets) &&
        widget.datasets.length > 0 &&
        CHART_TYPES_MULTI_DATASET.includes(widget.type)
      ) {
        const results = await Promise.all(
          widget.datasets.map(async (ds) => {
            const m = ds.metric || 'cpu';
            const res = await API.get(`/api/monitoring/metrics/${m}`);
            return { ds, data: res.data };
          })
        );
        const usedMetricKeys = new Set();
        const metricSeries = results.map(({ ds, data }, idx) => {
          let dataKey = sanitizeSeriesDataKey(ds);
          if (usedMetricKeys.has(dataKey)) {
            dataKey = `${dataKey}__${idx}`;
          }
          usedMetricKeys.add(dataKey);
          return {
            dataKey,
            label: ds.label || ds.id,
            color: ds.color || 'blue',
            metric: ds.metric || 'cpu',
            response: data
          };
        });
        setData({
          __multiMetricDatasets: true,
          series: metricSeries
        });
        setLoading(false);
        return;
      }
      
      // For non-logs data sources, check if custom endpoint is configured and valid
      if (widget.useCustomEndpoint && widget.customEndpoint && widget.customEndpoint.trim() !== '') {
        try {
          console.log('Attempting to fetch from custom endpoint:', widget.customEndpoint);
          // Proxy through backend to avoid CORS issues
          response = await API.post('/api/monitoring/proxy', {
            url: widget.customEndpoint,
            method: 'GET'
          });
          
          console.log('Raw custom endpoint response data:', response.data);
          const transformedData = transformCustomEndpointData(response.data, widget);
          console.log('Transformed data:', transformedData);
          setData(transformedData);
          setLoading(false);
          return;
        } catch (customErr) {
          console.warn('Failed to fetch from custom endpoint:', customErr);
          console.warn('Falling back to default metrics endpoint');
          // Fall through to use default metrics endpoint instead of showing error
          // This allows widgets to work even if custom endpoint is unavailable
        }
      }
      
      // Default endpoints (for metrics or when dataSource is not set)
      // Note: logs dataSource is handled earlier and returns early
      if (widget.dataSource === 'metrics' || !widget.dataSource) {
        // Default to metrics if dataSource is not set
        if (widget.metric) {
          response = await API.get(`/api/monitoring/metrics/${widget.metric}`);
        } else {
          response = await API.get('/api/monitoring/metrics');
        }
      } else {
        // Unknown data source, try to fetch metrics as fallback
        console.warn(`Unknown dataSource: ${widget.dataSource}, falling back to metrics`);
        response = await API.get('/api/monitoring/metrics');
      }

      if (response && response.data) {
        console.log('API response data:', response.data);
        const dataToSet = response.data;
        if (dataToSet.logs && Array.isArray(dataToSet.logs)) {
          console.log('Logs data structure:', {
            hasLogs: !!dataToSet.logs,
            logsLength: dataToSet.logs?.length || 0,
            total: dataToSet.total,
            dataKeys: Object.keys(dataToSet),
            widgetDataSource: widget.dataSource,
            widgetType: widget.type,
            firstLog: dataToSet.logs?.[0]
          });
        }
        setData(dataToSet);
      } else {
        console.warn('No data received from server');
        setError('No data received from server');
        setData({});
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching widget data:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to fetch data';
      const token = localStorage.getItem('token');
      
      if (err.code === 'ECONNREFUSED' || err.message?.includes('connection refused') || err.message?.includes('Network Error')) {
        // Check if it's actually a connection issue or authentication issue
        if (!token) {
          errorMessage = 'Authentication required. Please log in to view dashboard data.';
        } else {
          errorMessage = 'Cannot connect to backend server. Please ensure the backend is running on http://localhost:8080';
        }
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        if (!token) {
          errorMessage = 'Authentication required. Please log in to view dashboard data.';
        } else {
          errorMessage = 'Authentication failed. Your session may have expired. Please log out and log in again.';
        }
      } else if (err.response?.status) {
        errorMessage = `Server error: ${err.response.status} - ${err.response.statusText || 'Unknown error'}`;
        if (err.response.data?.error) {
          errorMessage += ` (${err.response.data.error})`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setData({});
      setLoading(false);
    }
  }, [
    widget,
    startTime,
    endTime,
    // Re-fetch when multi-series config changes even if parent reuses the same object reference
    JSON.stringify(widget?.datasets ?? []),
    JSON.stringify(widget?.customTableColumns ?? [])
  ]);

  useEffect(() => {
    if (!widget) {
      setError('Widget configuration is missing');
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, widget]);

  // Manual refresh when refreshTrigger changes (no auto-refresh)
  useEffect(() => {
    if (typeof refreshTrigger === 'number' && refreshTrigger > 0 && widget) {
      fetchData();
    }
  }, [refreshTrigger, widget, fetchData]);

  // Debug: Log data changes
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      console.log('🔄 WidgetRenderer data state changed:', {
        widgetId: widget?.id,
        widgetType: widget?.type,
        dataSource: widget?.dataSource,
        dataKeys: Object.keys(data),
        hasLogs: !!data.logs,
        logsLength: data.logs?.length || 0,
        loading,
        error
      });
    }
  }, [data, widget, loading, error]);

  // Safety check for invalid widget - after hooks
  if (!widget) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-title">Invalid Widget</div>
          <div className="error-message">Widget configuration is missing</div>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    console.log('🟢 renderChart() called', {
      widgetType: widget?.type,
      loading,
      error,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : []
    });
    
    if (loading) {
      console.log('⏳ Showing loading spinner');
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      );
    }

    if (error) {
      console.log('❌ Showing error:', error);
      return (
        <div className="error-container">
          <div className="error-content">
            <div className="error-title">Error loading data</div>
            <div className="error-message">{error}</div>
            {error.includes('connection refused') || error.includes('ECONNREFUSED') ? (
              <div className="error-details" style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                <div>• Check if backend is running on http://localhost:8080</div>
                <div>• Verify network connectivity</div>
                <div>• Check browser console for detailed error</div>
              </div>
            ) : error.includes('Authentication') || error.includes('401') || error.includes('403') ? (
              <div className="error-details" style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                <div>• Authentication token may be missing or expired</div>
                <div>• Please log out and log in again</div>
              </div>
            ) : (
              <div className="error-details" style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                <div>Widget: {widget?.title || 'Unknown'}</div>
                <div>Type: {widget?.type || 'Unknown'}</div>
                <div>DataSource: {widget?.dataSource || 'Not set'}</div>
              </div>
            )}
          </div>
        </div>
      );
    }

    switch (widget.type) {
      case 'line-chart':
        return renderLineChart();
      case 'area-chart':
        return renderAreaChart();
      case 'bar-chart':
        return renderBarChart();
      case 'horizontal-bar-chart':
        return renderHorizontalBarChart();
      case 'pie-chart':
        return renderPieChart();
      case 'donut-chart':
        return renderDonutChart();
      case 'single-metric':
        return renderSingleMetric();
      case 'log-table':
        return renderLogTable();
      case 'custom-table':
        return renderCustomTable();
      default:
        return <div className="text-gray-500">Unknown widget type</div>;
    }
  };

  const renderLineChart = () => {
    let chartData = [];
    let isLogsDataProcessed = false;
    let seriesKeys = [];

    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }

    // Handle logs data source FIRST (before metrics/custom endpoint logic)
    // Auto-detect logs data - if response has logs array, treat it as logs data
    const hasLogsData = data && data.logs && Array.isArray(data.logs) && data.logs.length > 0;
    const isLogsDataSource = widget.dataSource === 'logs';

    let seriesMeta = null;
    let fromMultiDataset = false;

    if (data.__multiLogDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMultiDatasetLogTimeSeries(
        data.series.map((s) => ({ dataKey: s.dataKey, logs: s.logs })),
        startTime,
        endTime
      );
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({
        dataKey: s.dataKey,
        label: s.label,
        color: s.color || 'blue'
      }));
      isLogsDataProcessed = true;
      fromMultiDataset = true;
    } else if (data.__multiMetricDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMetricDatasetTimeSeries(data.series);
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({
        dataKey: s.dataKey,
        label: s.label,
        color: s.color || 'blue'
      }));
      isLogsDataProcessed = true;
      fromMultiDataset = true;
    } else if (hasLogsData || isLogsDataSource) {
      if (hasLogsData) {
        const pad = (num) => String(num).padStart(2, '0');
        if (widget.groupBy === 'level' || widget.groupBy === 'service') {
          const multi = groupLogsByTimeIntervalMultiSeries(data.logs, startTime, endTime, pad, widget.groupBy);
          chartData = multi.chartData || [];
          seriesKeys = multi.seriesKeys || [];
          isLogsDataProcessed = true;
        } else {
          const result = groupLogsByTimeInterval(data.logs, startTime, endTime, pad);
          chartData = result.chartData;
          isLogsDataProcessed = result.isLogsDataProcessed;
        }

        if (chartData.length > 0 && seriesKeys.length === 0) {
          console.log('✅ Logs chart data generated:', { dataPoints: chartData.length, sample: chartData.slice(0, 3) });
        } else if (chartData.length > 0 && seriesKeys.length > 0) {
          console.log('✅ Multi-series chart data:', { dataPoints: chartData.length, series: seriesKeys });
        } else if (data.logs.length > 0 && chartData.length === 0) {
          // Fallback: if grouping failed but we have logs, create a simple time series
          console.warn('⚠️ Grouping failed, creating simple time series from log indices');
          chartData = data.logs.slice(0, Math.min(100, data.logs.length)).map((log, index) => {
            const fallbackTime = Date.now() - (data.logs.length - index) * 60000;
            const date = new Date(fallbackTime);
            const pad = (n) => String(n).padStart(2, '0');
            const timeKey = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
            return {
              timestamp: fallbackTime,
              time: timeKey,
              total: 1
            };
          });
          isLogsDataProcessed = true;
          console.log('✅ Fallback chart data created:', chartData.length, 'points');
        } else {
          console.error('❌ No logs could be processed. All timestamps failed parsing.');
          console.error('Sample log entry:', data.logs[0]);
        }
      } else {
        console.warn('No logs data available:', {
          hasData: !!data,
          hasLogs: !!data?.logs,
          isArray: Array.isArray(data?.logs),
          length: data?.logs?.length || 0,
          dataStructure: data ? Object.keys(data) : 'no data'
        });
      }
    }
    // Handle metrics or custom endpoint data sources
    else if (!fromMultiDataset && (widget.dataSource === 'metrics' || widget.useCustomEndpoint || !widget.dataSource)) {
      // Handle custom endpoint data
      if (widget.useCustomEndpoint && data) {
        if (widget.metric && data[widget.metric]) {
          // Convert timestamps to numbers
          const metricData = data[widget.metric].data || [];
          chartData = metricData.map(item => ({
            ...item,
            timestamp: convertTimestamp(item.timestamp)
          }));
        } else if (data.cpu?.data) {
          // Use CPU data from custom endpoint and convert timestamps
          chartData = (data.cpu.data || []).map(item => ({
            ...item,
            timestamp: convertTimestamp(item.timestamp)
          }));
        } else {
          // Try to extract data from custom endpoint response
          chartData = (data.cpu?.data || []).map((item, index) => ({
            timestamp: convertTimestamp(item.timestamp || item.time || Date.now()),
            cpu: item.usage || item.value || 0,
            memory: (data.memory?.data || [])[index]?.used || (data.memory?.data || [])[index]?.value || 0,
            disk: (data.disk?.data || [])[index]?.percentage || (data.disk?.data || [])[index]?.value || 0
          }));
        }
      } 
      // Handle metrics data source
      else if (widget.dataSource === 'metrics' || !widget.dataSource) {
        if (widget.metric && data[widget.metric]) {
          // Convert timestamps to numbers for the selected metric
          const metricData = data[widget.metric].data || [];
          chartData = metricData.map(item => ({
            ...item,
            timestamp: convertTimestamp(item.timestamp)
          }));
        } else if (data && typeof data === 'object') {
          // Combine multiple metrics
          const cpuData = data.cpu?.data || [];
          const memoryData = data.memory?.data || [];
          const diskData = data.disk?.data || [];
          
          const maxLength = Math.max(cpuData.length, memoryData.length, diskData.length);
          
          if (maxLength > 0) {
            chartData = Array.from({ length: maxLength }, (_, index) => ({
              timestamp: convertTimestamp(
                cpuData[index]?.timestamp || 
                memoryData[index]?.timestamp || 
                diskData[index]?.timestamp || 
                Date.now() - (maxLength - index) * 60000
              ),
              cpu: cpuData[index]?.usage || cpuData[index]?.value || 0,
              memory: memoryData[index]?.used || memoryData[index]?.value || 0,
              disk: diskData[index]?.percentage || diskData[index]?.value || 0
            }));
          } else if (data.cpu || data.memory || data.disk) {
            // Handle single metric values (not time series)
            chartData = [{
              timestamp: Date.now(),
              cpu: data.cpu?.current || data.cpu?.usage || 0,
              memory: data.memory?.percentage || data.memory?.used || 0,
              disk: data.disk?.percentage || data.disk?.value || 0
            }];
          }
        }
      }
    }
    
    // Sort chart data by timestamp and ensure time labels exist
    // BUT skip this if logs data was already processed (it's already sorted and formatted)
    if (!isLogsDataProcessed && chartData && chartData.length > 0) {
      // Check if timestamps are already numbers (from logs processing)
      const firstTimestamp = chartData[0]?.timestamp;
      if (typeof firstTimestamp === 'number') {
        // Already numbers, ensure sorted and add time labels if missing
        chartData = [...chartData]
          .sort((a, b) => a.timestamp - b.timestamp)
          .map(item => ({
            ...item,
            time: item.time || formatWidgetChartTimeKey(new Date(item.timestamp), 1),
            timeLabel: item.timeLabel || formatDateTimeCompactUser(new Date(item.timestamp))
          }));
      } else {
        // Need to convert timestamps
        chartData = chartData
          .map(item => {
            const ts = convertTimestamp(item.timestamp);
            return {
              ...item,
              timestamp: ts,
              time: item.time || formatTimeShortUser(new Date(ts)),
              timeLabel: item.timeLabel || formatDateTimeCompactUser(new Date(ts))
            };
          })
          .sort((a, b) => a.timestamp - b.timestamp);
      }
    }
    
    console.log('📊 Final line chart data before render:', {
      length: chartData?.length || 0,
      sample: chartData?.slice(0, 3),
      firstItemKeys: chartData?.[0] ? Object.keys(chartData[0]) : [],
      hasCount: chartData?.[0]?.count !== undefined,
      hasTotal: chartData?.[0]?.total !== undefined,
      hasCpu: chartData?.[0]?.cpu !== undefined,
      fullFirstItem: chartData?.[0],
      widgetDataSource: widget.dataSource,
      widgetType: widget.type,
      isLogsDataProcessed: isLogsDataProcessed,
      fullChartData: JSON.stringify(chartData)
    });

    // Show empty state if no data
    if (!chartData || chartData.length === 0) {
      return (
        <div className="no-data">
          <p>No data available</p>
          <p className="text-gray-500" style={{ fontSize: '12px' }}>
            {widget.dataSource ? `DataSource: ${widget.dataSource}` : 'No data source configured'}
          </p>
          {data && (
            <div className="text-gray-400" style={{ fontSize: '10px', marginTop: '8px' }}>
              <p>Data received: {JSON.stringify(data).substring(0, 100)}...</p>
              {data.logs && (
                <p>Logs array length: {data.logs.length || 0}</p>
              )}
              {!data.logs && (
                <p>No logs array in data. Data keys: {Object.keys(data).join(', ')}</p>
              )}
            </div>
          )}
        </div>
      );
    }

    // Log actual chart data being passed to Recharts
    console.log('🎯 Passing to LineChart component:', {
      dataLength: chartData.length,
      fullData: chartData,
      firstItem: chartData[0],
      lastItem: chartData[chartData.length - 1],
      allTimestamps: chartData.map(d => d.timestamp),
      allCounts: chartData.map(d => d.count),
      hasTimeField: chartData[0]?.time !== undefined
    });

    // Single-series line needs 2+ points; multi-series can show one time bucket with several values
    if (chartData.length < 1) {
      return (
        <div className="no-data">
          <p>No data available for chart</p>
        </div>
      );
    }
    if (seriesKeys.length === 0 && chartData.length < 2) {
      console.warn('⚠️ Not enough data points for line chart. Need at least 2, got:', chartData.length);
      return (
        <div className="no-data">
          <p>Insufficient data for chart</p>
          <p className="text-gray-500" style={{ fontSize: '12px' }}>
            Need at least 2 time buckets for a single series, got {chartData.length}
          </p>
        </div>
      );
    }

    // Verify chartData is valid array with expected structure
    if (!Array.isArray(chartData) || chartData.length === 0) {
      console.error('❌ Chart data is invalid or empty:', {
        isArray: Array.isArray(chartData),
        length: chartData?.length || 0,
        chartData: chartData,
        data: data,
        widget: widget
      });
      return (
        <div className="no-data">
          <p>No data available for chart</p>
          <p className="text-gray-500" style={{ fontSize: '12px' }}>
            Chart data array is empty (length: {chartData?.length || 0})
          </p>
        </div>
      );
    }

    // Log what we're about to render
    console.log('🎨 Rendering LineChart with data:', {
      dataPoints: chartData.length,
      firstItem: chartData[0],
      dataKeys: chartData[0] ? Object.keys(chartData[0]) : [],
      sampleData: chartData.slice(0, 3),
      fullData: chartData,
      hasTime: chartData[0]?.time !== undefined,
      hasTotal: chartData[0]?.total !== undefined,
      timeValue: chartData[0]?.time,
      totalValue: chartData[0]?.total
    });

    // Multi-series: use chartData as-is; single-series: validate and map to total/time
    let renderData;
    if (seriesKeys.length > 0) {
      renderData = chartData.filter(d => d && d.time);
      if (renderData.length < 1) {
        return (
          <div className="no-data">
            <p>Insufficient data for chart</p>
            <p className="text-gray-500" style={{ fontSize: '12px' }}>No time buckets with data for the selected range.</p>
          </div>
        );
      }
    } else {
      const validatedData = chartData.map(item => {
        const timeValue = item.time || item.timeLabel || (item.timestamp ? formatDateTimeCompactUser(new Date(item.timestamp)) : '');
        const totalValue = typeof item.total === 'number' ? item.total
          : (typeof item.count === 'number' ? item.count
          : (typeof item.cpu === 'number' ? item.cpu
          : (typeof item.usage === 'number' ? item.usage
          : (typeof item.memory === 'number' ? item.memory
          : (typeof item.disk === 'number' ? item.disk
          : 0)))));
        return {
          ...item,
          time: String(timeValue),
          total: Number(totalValue)
        };
      }).filter(item => (item.total > 0 || item.time) && item.time);

      if (validatedData.length === 0) {
        return (
          <div className="no-data">
            <p>No valid data points</p>
            <p className="text-gray-500" style={{ fontSize: '12px' }}>Data could not be processed into chart format.</p>
          </div>
        );
      }

      renderData = validatedData.filter(d => {
        const hasTime = d.time && typeof d.time === 'string' && d.time.length > 0;
        const hasTotal = typeof d.total === 'number' && !isNaN(d.total) && d.total >= 0;
        return hasTime && hasTotal;
      });

      if (renderData.length === 0) {
        return (
          <div className="no-data">
            <p>No valid data after filtering</p>
          </div>
        );
      }
    }

    const isMultiSeries = seriesKeys.length > 0;
    const syncProps = getTimeSeriesSyncProps(renderData);

    return (
      <div className="chart-responsive">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <LineChart
              data={renderData}
              margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              syncId={syncProps?.syncId}
              syncMethod={syncProps?.syncMethod}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={syncProps ? 'timestamp' : 'time'}
                tickFormatter={syncProps?.tickFormatter}
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 10 }}
              />
              <YAxis domain={isMultiSeries ? ['auto', 'auto'] : ['dataMin - 50', 'dataMax + 50']} />
              <Tooltip
                cursor={syncProps ? SYNCED_CHART_CURSOR : true}
                labelFormatter={syncProps?.labelFormatter}
              />
              {isMultiSeries ? (
                <>
                  {seriesKeys.map((key, i) => {
                    const meta = seriesMeta?.find((m) => m.dataKey === key);
                    const stroke = meta ? getChartColor(meta.color) : SERIES_PALETTE[i % SERIES_PALETTE.length];
                    return (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={stroke}
                        strokeWidth={2}
                        name={meta?.label || key}
                        dot={false}
                        activeDot={{ r: 6, fill: stroke }}
                      />
                    );
                  })}
                  <Legend />
                </>
              ) : (
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke={getChartColor(widget.chartColor)}
                  strokeWidth={3}
                  name="Total Logs"
                  dot={false}
                  activeDot={{ r: 7, fill: getChartColor(widget.chartColor) }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderAreaChart = () => {
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }
    let chartData = [];
    let seriesKeys = [];
    let seriesMeta = null;
    const hasLogsData = data && data.logs && Array.isArray(data.logs) && data.logs.length > 0;
    const isLogsDataSource = widget.dataSource === 'logs';

    if (data.__multiLogDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMultiDatasetLogTimeSeries(
        data.series.map((s) => ({ dataKey: s.dataKey, logs: s.logs })),
        startTime,
        endTime
      );
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({ dataKey: s.dataKey, label: s.label, color: s.color || 'blue' }));
    } else if (data.__multiMetricDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMetricDatasetTimeSeries(data.series);
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({ dataKey: s.dataKey, label: s.label, color: s.color || 'blue' }));
    } else if (hasLogsData || isLogsDataSource) {
      const pad = (num) => String(num).padStart(2, '0');
      if (widget.groupBy === 'level' || widget.groupBy === 'service') {
        const multi = groupLogsByTimeIntervalMultiSeries(data.logs || [], startTime, endTime, pad, widget.groupBy);
        chartData = multi.chartData || [];
        seriesKeys = multi.seriesKeys || [];
      } else {
        const result = groupLogsByTimeInterval(data.logs || [], startTime, endTime, pad);
        chartData = result.chartData || [];
      }
    } else if (widget.dataSource === 'metrics' || widget.useCustomEndpoint || !widget.dataSource) {
      if (widget.metric && data[widget.metric]) {
        const metricData = data[widget.metric].data || [];
        chartData = metricData.map(item => ({ ...item, timestamp: convertTimestamp(item.timestamp), total: item.usage || item.value || 0 }));
      } else if (data.cpu?.data) {
        chartData = (data.cpu.data || []).map(item => ({ timestamp: convertTimestamp(item.timestamp), total: item.usage || item.value || 0 }));
      }
      const validated = (chartData || []).map(item => ({
        ...item,
        time: item.time || item.name || (item.timestamp ? formatDateTimeCompactUser(new Date(item.timestamp)) : ''),
        total: typeof item.total === 'number' ? item.total : (item.cpu ?? item.usage ?? item.value ?? 0)
      })).filter(item => item.time && typeof item.total === 'number' && !isNaN(item.total));
      chartData = validated;
    }

    if (!chartData || chartData.length < 1) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }
    if (seriesKeys.length === 0 && chartData.length < 2) {
      return (
        <div className="no-data">
          <p>No data available</p>
          <p className="text-gray-500" style={{ fontSize: '12px' }}>Need at least 2 time buckets for a single series.</p>
        </div>
      );
    }

    const isMultiSeries = seriesKeys.length > 0;
    const color = getChartColor(widget.chartColor);
    const syncProps = getTimeSeriesSyncProps(chartData);

    return (
      <div className="chart-responsive">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <AreaChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              syncId={syncProps?.syncId}
              syncMethod={syncProps?.syncMethod}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={syncProps ? 'timestamp' : 'time'}
                tickFormatter={syncProps?.tickFormatter}
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 10 }}
              />
              <YAxis domain={isMultiSeries ? ['auto', 'auto'] : ['dataMin - 50', 'dataMax + 50']} />
              <Tooltip
                cursor={syncProps ? SYNCED_CHART_CURSOR : true}
                labelFormatter={syncProps?.labelFormatter}
              />
              {isMultiSeries ? (
                <>
                  {seriesKeys.map((key, i) => {
                    const meta = seriesMeta?.find((m) => m.dataKey === key);
                    const c = meta ? getChartColor(meta.color) : SERIES_PALETTE[i % SERIES_PALETTE.length];
                    return (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={c}
                        fill={c}
                        fillOpacity={0.5}
                        name={meta?.label || key}
                      />
                    );
                  })}
                  <Legend />
                </>
              ) : (
                <Area type="monotone" dataKey="total" stroke={color} fill={color} fillOpacity={0.5} name="Total Logs" />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderDonutChart = () => {
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }
    let chartData = [];
    if (widget.dataSource === 'logs') {
      chartData = buildLogsPieSlices(data);
    } else if (data.__multiMetricDatasets && Array.isArray(data.series) && data.series.length > 0) {
      chartData = data.series.map((s) => ({
        name: s.label || s.metric || 'Series',
        value: metricSnapshotValue(s.metric, s.response)
      }));
    } else {
      chartData = [
        { name: 'CPU', value: data.cpu?.current || 0 },
        { name: 'Memory', value: data.memory?.percentage || 0 },
        { name: 'Disk', value: data.disk?.percentage || 0 }
      ];
    }
    if (!chartData || chartData.length === 0) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }
    const baseColor = getChartColor(widget.chartColor);
    const COLORS = [baseColor, '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
    return (
      <div className="chart-responsive">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                fill={baseColor}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.seriesColor != null ? getChartColor(entry.seriesColor) : COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderHorizontalBarChart = () => {
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }
    let chartData = [];
    let isLogsDataProcessed = false;
    let seriesKeys = [];
    let seriesMeta = null;
    const hasLogsData = data && data.logs && Array.isArray(data.logs) && data.logs.length > 0;
    const isLogsDataSource = widget.dataSource === 'logs';
    if (data.__multiLogDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMultiDatasetLogTimeSeries(
        data.series.map((s) => ({ dataKey: s.dataKey, logs: s.logs })),
        startTime,
        endTime
      );
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({ dataKey: s.dataKey, label: s.label, color: s.color || 'blue' }));
      isLogsDataProcessed = true;
    } else if (data.__multiMetricDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMetricDatasetTimeSeries(data.series);
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({ dataKey: s.dataKey, label: s.label, color: s.color || 'blue' }));
      isLogsDataProcessed = true;
    } else if (hasLogsData || isLogsDataSource) {
      const pad = (num) => String(num).padStart(2, '0');
      if (widget.groupBy === 'level' || widget.groupBy === 'service') {
        const multi = groupLogsByTimeIntervalMultiSeries(data.logs, startTime, endTime, pad, widget.groupBy);
        chartData = multi.chartData || [];
        seriesKeys = multi.seriesKeys || [];
        isLogsDataProcessed = true;
      } else {
        const result = groupLogsByTimeInterval(data.logs, startTime, endTime, pad);
        chartData = result.chartData;
        isLogsDataProcessed = result.isLogsDataProcessed;
      }
    } else if (widget.dataSource === 'metrics') {
      chartData = [
        { name: 'CPU', value: data.cpu?.current || 0 },
        { name: 'Memory', value: data.memory?.percentage || 0 },
        { name: 'Disk', value: data.disk?.percentage || 0 }
      ];
    }
    if (!chartData || chartData.length === 0) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }
    const isMultiSeries = seriesKeys.length > 0;
    const syncProps = isLogsDataProcessed ? getTimeSeriesSyncProps(chartData) : null;
    const categoryDataKey = syncProps ? 'timestamp' : (isLogsDataProcessed ? 'time' : 'name');
    const barDataKey = isLogsDataProcessed ? 'total' : 'value';
    const color = getChartColor(widget.chartColor);
    return (
      <div className="chart-responsive">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
              syncId={syncProps?.syncId}
              syncMethod={syncProps?.syncMethod}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis
                type="category"
                dataKey={categoryDataKey}
                tickFormatter={syncProps?.tickFormatter}
                width={70}
                tick={{ fontSize: 10 }}
              />
              <Tooltip
                cursor={syncProps ? SYNCED_CHART_CURSOR : true}
                labelFormatter={syncProps?.labelFormatter}
              />
              {isMultiSeries ? (
                <>
                  {seriesKeys.map((key, i) => {
                    const meta = seriesMeta?.find((m) => m.dataKey === key);
                    const fill = meta ? getChartColor(meta.color) : SERIES_PALETTE[i % SERIES_PALETTE.length];
                    return (
                      <Bar key={key} dataKey={key} fill={fill} name={meta?.label || key} stackId="a" />
                    );
                  })}
                  <Legend />
                </>
              ) : (
                <Bar dataKey={barDataKey} fill={color} name={isLogsDataProcessed ? 'Total Logs' : 'Value'} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderBarChart = () => {
    let chartData = [];
    let isLogsDataProcessed = false;
    let seriesKeys = [];
    let seriesMeta = null;

    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }

    const hasLogsData = data && data.logs && Array.isArray(data.logs) && data.logs.length > 0;
    const isLogsDataSource = widget.dataSource === 'logs';

    if (data.__multiLogDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMultiDatasetLogTimeSeries(
        data.series.map((s) => ({ dataKey: s.dataKey, logs: s.logs })),
        startTime,
        endTime
      );
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({ dataKey: s.dataKey, label: s.label, color: s.color || 'blue' }));
      isLogsDataProcessed = true;
    } else if (data.__multiMetricDatasets && Array.isArray(data.series) && data.series.length > 0) {
      const merged = mergeMetricDatasetTimeSeries(data.series);
      chartData = merged.chartData;
      seriesKeys = merged.seriesKeys;
      seriesMeta = data.series.map((s) => ({ dataKey: s.dataKey, label: s.label, color: s.color || 'blue' }));
      isLogsDataProcessed = true;
    } else if (hasLogsData || isLogsDataSource) {
      const pad = (num) => String(num).padStart(2, '0');
      if (widget.groupBy === 'level' || widget.groupBy === 'service') {
        const multi = groupLogsByTimeIntervalMultiSeries(data.logs, startTime, endTime, pad, widget.groupBy);
        chartData = multi.chartData || [];
        seriesKeys = multi.seriesKeys || [];
        isLogsDataProcessed = true;
      } else {
        const result = groupLogsByTimeInterval(data.logs, startTime, endTime, pad);
        chartData = result.chartData;
        isLogsDataProcessed = result.isLogsDataProcessed;
      }
    } else if (widget.dataSource === 'metrics') {
      chartData = [
        { name: 'CPU', value: data.cpu?.current || 0 },
        { name: 'Memory', value: data.memory?.percentage || 0 },
        { name: 'Disk', value: data.disk?.percentage || 0 }
      ];
    }

    if (!chartData || chartData.length === 0) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }

    const isMultiSeries = seriesKeys.length > 0;
    const syncProps = isLogsDataProcessed ? getTimeSeriesSyncProps(chartData) : null;
    const xAxisDataKey = syncProps ? 'timestamp' : (isLogsDataProcessed ? 'time' : 'name');
    const barDataKey = isLogsDataProcessed ? 'total' : 'value';

    return (
      <div className="chart-responsive">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <BarChart
              data={chartData}
              barCategoryGap="15%"
              barGap={4}
              margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              syncId={syncProps?.syncId}
              syncMethod={syncProps?.syncMethod}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey={xAxisDataKey}
                tickFormatter={syncProps?.tickFormatter}
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 10 }}
              />
              <YAxis />
              <Tooltip
                cursor={syncProps ? SYNCED_CHART_CURSOR : true}
                labelFormatter={syncProps?.labelFormatter}
              />
              {isMultiSeries ? (
                <>
                  {seriesKeys.map((key, i) => {
                    const meta = seriesMeta?.find((m) => m.dataKey === key);
                    const fill = meta ? getChartColor(meta.color) : SERIES_PALETTE[i % SERIES_PALETTE.length];
                    return (
                      <Bar key={key} dataKey={key} fill={fill} name={meta?.label || key} stackId="a" />
                    );
                  })}
                  <Legend />
                </>
              ) : (
                <Bar dataKey={barDataKey} fill={getChartColor(widget.chartColor)} name={isLogsDataProcessed ? 'Total Logs' : 'Value'} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderPieChart = () => {
    let chartData = [];
    
    if (widget.dataSource === 'logs') {
      chartData = buildLogsPieSlices(data);
    } else if (data.__multiMetricDatasets && Array.isArray(data.series) && data.series.length > 0) {
      chartData = data.series.map((s) => ({
        name: s.label || s.metric || 'Series',
        value: metricSnapshotValue(s.metric, s.response)
      }));
    } else {
      chartData = [
        { name: 'CPU', value: data.cpu?.current || 0 },
        { name: 'Memory', value: data.memory?.percentage || 0 },
        { name: 'Disk', value: data.disk?.percentage || 0 }
      ];
    }

    if (!chartData || chartData.length === 0) {
      return (
        <div className="no-data">
          <p>No data available</p>
        </div>
      );
    }

    const baseColor = getChartColor(widget.chartColor);
    const COLORS = [baseColor, '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

    return (
      <div className="chart-responsive">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill={baseColor}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.seriesColor != null ? getChartColor(entry.seriesColor) : COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            <Tooltip />
          </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderSingleMetric = () => {
    let value = 0;
    let unit = '%';
    let label = widget.title || 'Metric';

    // Use static value from widget config (e.g. sample "Total Requests" with value "1,234")
    if (widget.value != null && widget.value !== '') {
      const parsed = parseFloat(String(widget.value).replace(/,/g, ''));
      if (!isNaN(parsed)) {
        value = parsed;
        unit = '';
      }
    }
    // Use API data when available and no static value
    else if (widget.dataSource === 'metrics' && data && (data.cpu || data.memory || data.disk)) {
      if (widget.metric === 'cpu') {
        value = data.cpu?.current ?? data.cpu?.usage ?? 0;
        label = 'CPU Usage';
      } else if (widget.metric === 'memory') {
        value = data.memory?.percentage ?? data.memory?.used ?? 0;
        label = 'Memory Usage';
      } else if (widget.metric === 'disk') {
        value = data.disk?.percentage ?? data.disk?.value ?? 0;
        label = 'Disk Usage';
      } else if (widget.metric === 'network') {
        value = data.network?.data?.[0]?.inbound || 0;
        unit = ' MB/s';
        label = 'Network In';
      } else {
        value = data.cpu?.current ?? data.memory?.percentage ?? data.disk?.percentage ?? 0;
      }
    }

    return (
      <div className="metric-container">
        <div className="metric-value">
          {value.toFixed(1)}{unit}
        </div>
        <div className="metric-label">{label}</div>
        <div className="metric-progress">
          <div 
            className="metric-progress-bar"
            style={{ width: `${Math.min(value, 100)}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const renderCustomTable = () => {
    const logs = data.logs || [];
    let columns = Array.isArray(widget.customTableColumns) && widget.customTableColumns.length > 0
      ? widget.customTableColumns.map(normalizeCustomTableColumn)
      : [
          { id: 't', header: 'Time', field: 'timestamp', fieldMode: 'preset', valueType: 'auto' },
          { id: 'l', header: 'Level', field: 'level', fieldMode: 'preset', valueType: 'auto' },
          { id: 'm', header: 'Message', field: 'message', fieldMode: 'preset', valueType: 'auto' }
        ];

    if (columns.length === 0) {
      return (
        <div className="no-data">
          Add at least one column in widget settings
        </div>
      );
    }

    if (logs.length === 0) {
      return (
        <div className="no-data">
          No logs found
        </div>
      );
    }

    const getLevelClass = (level) => {
      switch (level) {
        case 'ERROR': return 'error';
        case 'WARN': return 'warn';
        case 'INFO': return 'info';
        case 'DEBUG': return 'debug';
        case 'TRACE': return 'trace';
        default: return 'debug';
      }
    };

    const displayRows = logs.slice(0, 500);

    return (
      <div className="log-table-container custom-table-widget-wrap">
        <table className="log-table log-table-widget custom-table-widget">
          <colgroup>
            {columns.map((col) => (
              <col key={col.id || col.field} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.id || col.field} className="log-col-head" scope="col">
                  {col.header || col.field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((log, index) => (
              <tr key={log.id != null ? String(log.id) : index}>
                {columns.map((col) => {
                  const field = col.field || 'message';
                  if (field === 'level') {
                    const lvl = getLogValueByPath(log, 'level') ?? log.level ?? '—';
                    return (
                      <td key={col.id || field} className="log-col-level">
                        <span className={`log-level ${getLevelClass(lvl)}`}>{String(lvl)}</span>
                      </td>
                    );
                  }
                  const { node, extraClass } = formatCustomTableCellContent(log, col);
                  const tdClass = customTableCellClassForColumn(col, extraClass);
                  return (
                    <td key={col.id || field} className={tdClass || undefined}>
                      {node}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLogTable = () => {
    const logs = data.logs || [];
    const showSeriesCol = logs.some((l) => l._datasetLabel);

    if (logs.length === 0) {
      return (
        <div className="no-data">
          No logs found
        </div>
      );
    }

    const getLevelClass = (level) => {
      switch (level) {
        case 'ERROR': return 'error';
        case 'WARN': return 'warn';
        case 'INFO': return 'info';
        case 'DEBUG': return 'debug';
        case 'TRACE': return 'trace';
        default: return 'debug';
      }
    };

    const formatTs = (log) => {
      const t = log.timestamp;
      try {
        if (t == null) return '—';
        return formatTimeShortUser(new Date(t));
      } catch {
        return String(t);
      }
    };

    return (
      <div className="log-table-container">
        <table
          className={`log-table log-table-widget${showSeriesCol ? ' log-table-widget--with-series' : ''}`}
        >
          <colgroup>
            <col className="log-w-time" />
            {showSeriesCol && <col className="log-w-series" />}
            <col className="log-w-level" />
            <col className="log-w-service" />
            <col className="log-w-message" />
            <col className="log-w-trace" />
          </colgroup>
          <thead>
            <tr>
              <th className="log-col-head log-col-time" scope="col">Time</th>
              {showSeriesCol && <th className="log-col-head log-col-series" scope="col">Series</th>}
              <th className="log-col-head log-col-level" scope="col">Level</th>
              <th className="log-col-head log-col-service" scope="col">Service</th>
              <th className="log-col-head log-col-message" scope="col">Message</th>
              <th className="log-col-head log-col-trace" scope="col">Trace ID</th>
            </tr>
          </thead>
          <tbody>
            {logs.slice(0, 100).map((log, index) => (
              <tr key={index}>
                <td className="log-col-time timestamp">{formatTs(log)}</td>
                {showSeriesCol && <td className="log-col-series log-service">{log._datasetLabel || '—'}</td>}
                <td className="log-col-level">
                  <span className={`log-level ${getLevelClass(log.level)}`}>
                    {log.level}
                  </span>
                </td>
                <td className="log-col-service log-service">{log.service || '—'}</td>
                <td className="log-col-message log-message">{log.message || '—'}</td>
                <td className="log-col-trace log-trace-id">{log.traceId || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="chart-container">
      {renderChart()}
    </div>
  );
};

export default WidgetRenderer;
